import React, { useEffect, useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import cx from 'classnames';
import { useSelector } from 'react-redux';

import { Link, Button } from '../../_shared';
import { MENU_ITEMS, MANAGER_MENU_ITEMS } from '../Header/constants';
import logo from '../../../assets/imgs/rabbit.png';
import { makeSelectUser } from '../../../store/selectors';
import { ROLE } from '../../../constants';
import { ThemeContext } from '../../../providers/ThemeProvider';
import { isEnterprise } from '../../../config';

const Container = styled.div.attrs({
  className: 'w-full bg-transparent m-auto mt-0',
})`
  max-width: 1440px;

  .header {
    ${tw`px-9 py-5 bg-white text-primary font-bold text-base tracking-widest mb-5`}
    border-radius: 15px;
    font-size: 20px;
    font-weight: 500;
  }

  .body {
    .sidebar {
      ${tw``}
      border-radius: 15px;
      min-width: 280px;

      a {
        ${tw`text-gray-200 my-2 text-base tracking-widest`}
        text-decoration: none;
        &.selected {
          ${tw`text-white font-bold`}

          svg {
            path {
              fill-opacity: 1;
            }
          }
        }
      }
    }

    .content {
      ${tw`pl-9 flex-grow tracking-widest py-2`}
      max-width: calc(100% - 280px);
    }
  }

  @media only screen and (max-width: 639px) {
    padding: 40px 20px;

    .header {
      padding: 13px 30px;
      font-weight: 500;
      font-size: 18px;
      line-height: 23px;
    }

    .body {
      .sidebar {
        padding: 30px 20px;

        a {
          ${tw`text-sm`}
        }
      }

      .content {
        ${tw`px-0 py-5`}
        width: 100%;
      }
    }
  }
`;

const AccountLayout = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const user = useSelector(makeSelectUser());
  const { primaryColor } = useContext(ThemeContext);

  useEffect(() => {
    if (!user) {
      history.push('/sign-in');
    }
  }, [user]);

  const handleResumeLearning = () => {
    const resumeLessonInfo = localStorage.getItem('resume_lesson_info');
    if (resumeLessonInfo) {
      const { curriculumId, programId, courseId } = JSON.parse(
        resumeLessonInfo
      );
      history.push(
        `/programs/${programId}/${curriculumId}/courses/${courseId}`
      );
    }
  };

  const handleGotoPrograms = () => {
    history.push('/programs');
  };

  return (
    <Container>
      <div className="header flex items-center justify-between">
        My Account
        {user &&
          user.role !== ROLE.Admin_Non_Student &&
          user.role !== ROLE.Manager && (
            <div className="flex items-center">
              <Button
                className="font-semibold px-4 py-2 mr-4 text-base complete-btn bg-light-yellow rounded-lg text-white font-bold"
                caption="View All Curricula"
                type="neutral"
                onClick={handleGotoPrograms}
              />
              <Button
                className="font-semibold px-4 py-2 text-base complete-btn bg-light-yellow rounded-lg text-white font-bold"
                caption="Return to Learning"
                type="secondary"
                onClick={handleResumeLearning}
              />
            </div>
          )}
      </div>
      <div className="body">
        <div className="flex items-start sm:flex-col">
          <div
            className="sidebar flex flex-col px-9 py-5 bg-primary sm:w-full sm:flex-row sm:flex-wrap"
            style={{ background: primaryColor }}
          >
            {user &&
              (user.role === ROLE.Manager ||
              user.role === ROLE.Admin_Non_Student
                ? MANAGER_MENU_ITEMS
                : user.role === ROLE.Admin || user.role === ROLE.Manager_Student
                ? MANAGER_MENU_ITEMS.concat(MENU_ITEMS)
                : MENU_ITEMS
              ).map((item) =>
                item.hidden ? null : item.external ? (
                  <div
                    key={item.key}
                    className="sm:w-1/2 sm:text-sm cursor-pointer text-gray-200 my-2 text-base tracking-widest flex items-center"
                    onClick={() => window.open(item.path, '_blank')}
                  >
                    {item.icon}
                    {item.label}
                  </div>
                ) : (
                  <Link
                    key={item.key}
                    to={item.path}
                    className={cx(
                      item.path === location.pathname && 'selected',
                      'sm:w-1/2 flex items-center'
                    )}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                )
              )}
            <div className="sm:hidden flex items-center justify-center p-8 mt-20">
              {!isEnterprise && (
                <img className="w-8 h-auto m-auto" src={logo} alt="logo" />
              )}
            </div>
          </div>
          {user && <div className="content">{children}</div>}
        </div>
      </div>
    </Container>
  );
};

export default AccountLayout;
